/**
 * TableViewerNova Component
 * 
 * A simplified version of Nova that accepts an engine and activeTable directly,
 * without showing the user a list of tables. Used for displaying table data in contexts
 * like the CSV viewer in the app.
 */

import * as React from 'react';
import { cn, toast } from '@/components/ui';
import { DataGrid } from '../data-grid/data-grid';
import { ColumnRef, Column } from '../data-grid/types';
import { 
  Engine, 
  TableRef, 
  MaterializedTableData, 
  Query, 
  QueryTypeEnum as QueryType,
  TableView,
  TableSchema,
  SQLDialect
} from '@/lib/engine';
import { NovaContainer } from '../nova-container/nova-container';
import { NovaExportButton } from '../nova-export-button';
import { useSubscription } from '@/hooks/use-subscription';
import { NovaStoreProvider, useNovaStoreContext, useNovaEngine } from '../nova/nova-store-context';
import { NovaAIChat } from '../nova-ai-chat';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { SubscriptionInfo } from '../nova-ai-chat/types';
import { useRouter } from 'next/navigation';
import { useUser } from '@/hooks/use-user';
import { Button } from '@/components/ui';
import { Loader2 } from 'lucide-react';
import { trackAction } from '@/lib/tracking';

// Helper function to convert ColumnRef to Column for DataGrid
function convertToColumns(columnRefs: ColumnRef[]): Column[] {
  if (!columnRefs || !Array.isArray(columnRefs)) {
    return [];
  }
  return columnRefs.map(ref => ({
    ...ref.schema,
    name: ref.name,
    header: ref.name,
    sortable: true,
    filterable: true,
    resizable: true
  }));
}

// Helper function to create ColumnRef objects from schema fields
function createColumnRefs(schema: TableSchema): ColumnRef[] {
  if (!schema || !schema.columns || !Array.isArray(schema.columns)) {
    return [];
  }
  
  return schema.columns.map(column => ({
    name: column.name,
    schema: {
      name: column.name,
      type: column.type?.toString() || 'string',
      nullable: column.nullable || false
    }
  }));
}

/**
 * TopRightActions Component
 * Combines export and save as dataset buttons for file viewer contexts
 */
interface TopRightActionsProps {
  engine: Engine;
  lastExecutedView?: TableView | null;
  onSaveAsDataset: () => Promise<void>;
  onUpgradeClick?: (args: { openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' | 'create_dataset' }) => void;
}

function TopRightActions({ 
  engine, 
  lastExecutedView, 
  onSaveAsDataset, 
  onUpgradeClick 
}: TopRightActionsProps) {
  const [isSavingDataset, setIsSavingDataset] = React.useState(false);
  const { user } = useUser();

  const handleSaveAsDataset = React.useCallback(async () => {
    try {
      // Check if user is logged in
      if (!user) {
        // Show upgrade modal for non-logged-in users
        if (onUpgradeClick) {
          onUpgradeClick({ openTrigger: 'create_dataset' });
        } else {
          toast.error("Please log in to save datasets", {
            description: "You need to be logged in to save your data as a dataset.",
            duration: 5000,
            closeButton: true,
          });
        }
        return;
      }
      
      // Check if we have a view to save
      if (!lastExecutedView) {
        toast.error("No data to save", {
          description: "Please run a query first to generate data to save.",
          duration: 5000,
          closeButton: true,
        });
        return;
      }
      
      setIsSavingDataset(true);
      await onSaveAsDataset();
    } catch (error) {
      console.error('Error saving dataset:', error);
      toast.error("Failed to save dataset", {
        description: error instanceof Error ? error.message : String(error),
        duration: 5000,
        closeButton: true,
      });
    } finally {
      setIsSavingDataset(false);
    }
  }, [user, lastExecutedView, onSaveAsDataset, onUpgradeClick]);

  return (
    <div className="flex items-center gap-2">
      {/* Export Button */}
      {lastExecutedView && (
        <NovaExportButton engine={engine} view={lastExecutedView} />
      )}
      
      {/* Save as Dataset Button */}
      <Button
        variant="outline"
        size="sm"
        className="h-8 px-2.5 flex items-center gap-1"
        onClick={handleSaveAsDataset}
        disabled={isSavingDataset}
        title="Save the current data as a new dataset"
      >
        {isSavingDataset ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>Saving...</span>
          </>
        ) : (
          <span>Save as Dataset</span>
        )}
      </Button>
    </div>
  );
}

export interface TableViewerNovaProps {
  /**
   * Engine instance to use for query execution
   */
  engine: Engine;

  /**
   * The active table to display
   */
  activeTable: TableRef;

  /**
   * Optional class name for the container
   */
  className?: string;
  
  /**
   * URL for the chat endpoint
   * @default '/api/chat'
   */
  chatEndpoint?: string;
  
  /**
   * Optional initial prompt for the AI chat
   */
  initialPrompt?: string;

  /**
   * Subscription information for the user
   * Used to control access to AI chat features based on subscription status
   */
  subscriptionInfo?: SubscriptionInfo;

  /**
   * Optional callback when the upgrade link is clicked
   * Used to show the upgrade modal or navigate to upgrade page
   * @param args Object containing the openTrigger source
   */
  onUpgradeClick?: (args: { openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' | 'create_dataset' }) => void;
}

/**
 * Inner component that uses the NovaStore
 */
function TableViewerNovaInner({ 
  activeTable, 
  className,
  chatEndpoint = '/api/chat',
  initialPrompt,
  subscriptionInfo,
  onUpgradeClick
}: Omit<TableViewerNovaProps, 'engine'>) {
  // Get the store from context
  const store = useNovaStoreContext();
  const engine = useNovaEngine();
  const router = useRouter();
  
  const {
    data,
    columns,
    columnRefs,
    originalColumnRefs,
    query,
    lastExecutedQuery,
    lastExecutedView,
    status,
    error,
    executeQuery: storeExecuteQuery,
    updateQuery,
    setActiveTable: storeSetActiveTable,
    logState,
    runSqlQuery
  } = useNovaStoreContext();

  // Single state object to track initialization status
  const [initState, setInitState] = React.useState({
    isInitialized: false,
    isLoading: true,
    error: null as Error | null
  });

  // Initialize the table when it changes
  React.useEffect(() => {
    let isMounted = true;

    const initializeTable = async () => {
      if (!activeTable) return;

      try {
        // Set initial loading state atomically
        setInitState({
          isInitialized: false,
          isLoading: true,
          error: null
        });

        // Initialize table in store
        await storeSetActiveTable(activeTable);

        // Update state atomically after successful initialization
        if (isMounted) {
          setInitState({
            isInitialized: true,
            isLoading: false,
            error: null
          });
        }
      } catch (err) {
        console.error('Error initializing table:', err);
        if (isMounted) {
          setInitState({
            isInitialized: false,
            isLoading: false,
            error: err instanceof Error ? err : new Error(String(err))
          });
          toast.error('Failed to initialize table data');
        }
      }
    };

    initializeTable();

    return () => {
      isMounted = false;
    };
  }, [activeTable, storeSetActiveTable]);

  // Execute queries
  const executeQuery = React.useCallback((queryToUse: Query | null = null) => {
    if (queryToUse || query) {
      return storeExecuteQuery(queryToUse || query);
    }
    return Promise.resolve();
  }, [query, storeExecuteQuery]);

  // Handle running SQL queries from the AI chat
  const handleRunSqlQuery = React.useCallback((sqlQuery: string) => {
    if (!sqlQuery) return;
    
    // If user doesn't have active subscription, show upgrade modal instead of running query
    if (subscriptionInfo && !subscriptionInfo.hasActiveSubscription && onUpgradeClick) {
      onUpgradeClick({ openTrigger: 'chat_suggestion' });
      return;
    }
    
    try {
      // Create a SQL query object
      const newQuery: Query = {
        type: QueryType.SQL,
        options: {
          sql: sqlQuery,
          dialect: SQLDialect.DUCKDB
        }
      };
      
      // Update and execute the query
      updateQuery(newQuery);
      executeQuery(newQuery);
      
      toast.success("Running SQL query from AI chat");
    } catch (err) {
      console.error("Error running SQL query:", err);
      toast.error("Failed to run SQL query: " + String(err));
    }
  }, [updateQuery, executeQuery, subscriptionInfo, onUpgradeClick]);

  // Handler for user attempting to submit a message in the chat when they're not subscribed
  const handleUpgradeFromChat = React.useCallback((args: { openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' }) => {
    if (onUpgradeClick) {
      onUpgradeClick(args);
    }
  }, [onUpgradeClick]);

  // Handle saving current data as dataset
  const handleSaveAsDataset = React.useCallback(async () => {
    if (!lastExecutedView) {
      throw new Error('No data to save');
    }
    
    const dataName = activeTable?.path || 'Nova Query Result';
    
    // Create a proper view that represents the current query state
    // This ensures we save the filtered/transformed data, not the original table
    let viewToExport = lastExecutedView;
    
    // If we have a current query that's different from what's in lastExecutedView,
    // create a new view with the current query
    if (query && lastExecutedView.source.type === 'query' && query !== lastExecutedView.source.query) {
      viewToExport = await engine.transform(await engine.getTableView(activeTable), query);
    } else if (query && lastExecutedView.source.type === 'table') {
      // If the lastExecutedView is still pointing to the raw table but we have a query,
      // transform it with the current query
      viewToExport = await engine.transform(await engine.getTableView(activeTable), query);
    }
    
    // Step 1: Get signed URL for temp storage
    const response = await fetch('/api/convert/urls', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sourceFormat: 'parquet',
        destinationFormat: 'parquet',
        filename: `${dataName}.parquet`
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to get upload URL');
    }

    const { source } = await response.json();
    const { uploadUrl, filePath } = source;
    
    // Step 2: Export current view data to the temp storage
    await engine.exportToURL({
      view: viewToExport,
      signedUrl: uploadUrl
    });
    
    // Step 3: Create dataset using create-from-files endpoint
    const createDatasetResponse = await fetch('/api/datasets/create-from-files', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: {
          name: dataName,
          short_description: `Dataset created from Nova query on ${new Date().toLocaleDateString()}`,
          visibility: 'link',
          files: [{
            name: `${dataName}.parquet`,
            format: 'parquet',
            tempPath: filePath
          }]
        }
      }),
    });

    if (!createDatasetResponse.ok) {
      const errorData = await createDatasetResponse.json();
      throw new Error(errorData.error || 'Failed to create dataset');
    }

    const dataset = await createDatasetResponse.json();
    
    // Track dataset creation
    trackAction('dataset_created', {
      source: 'upload',
      datasetId: dataset.id,
      datasetName: dataset.name
    });
    
    // Step 4: Redirect to the new dataset
    router.push(`/datasets/${dataset.id}/${dataset.slug}`);
    
    toast.success('Dataset saved successfully!', {
      description: `Your data has been saved as "${dataset.name}"`,
      duration: 5000,
      closeButton: true,
    });
  }, [lastExecutedView, activeTable, engine, router, query]);

  // Check if data and columns are ready
  const isDataReady = React.useMemo(() => {
    return initState.isInitialized && 
           data !== null && 
           columnRefs.length > 0 && 
           originalColumnRefs.length > 0;
  }, [initState.isInitialized, data, columnRefs, originalColumnRefs]);

  // For the table initialization loading screen, we only want to show it when
  // we're actually initializing a new table - but only if we have no data at all
  const shouldShowInitializing = React.useMemo(() => {
    // Don't show loading if we're just waiting for data but have basic props
    // The user should see the interface immediately
    return false;
  }, []);

  // Render content based on state
  const renderContent = () => {
    // If we have no data yet but are loading, show a subtle loading state within the grid area
    if ((!data || !columns || columns.length === 0) && status === 'loading') {
      return (
        <div className="flex h-full w-full items-center justify-center p-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading table data...</p>
          </div>
        </div>
      );
    }
    
    // If we have no data yet, show an empty state
    if (!data || !columns || columns.length === 0) {
      return (
        <div className="flex h-full w-full items-center justify-center p-4">
          <div className="text-center">
            <h3 className="text-lg font-medium mb-2">No Data Available</h3>
            <p>Unable to display the table without data or columns.</p>
          </div>
        </div>
      );
    }
    
    // If we have error, overlay it on the grid
    if (error) {
      return (
        <div className="relative h-full w-full">
          <DataGrid
            columns={columns}
            data={data}
            loading={false}
            error={error}
            query={query}
            updateQuery={updateQuery}
            executeQuery={executeQuery}
            lastExecutedQuery={lastExecutedQuery}
            height="100%"
            width="100%"
          />
          <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
            <div className="text-center text-destructive bg-background p-4 rounded-lg shadow-md border border-destructive/30">
              <h3 className="text-lg font-medium mb-2">Error</h3>
              <p>{error.message}</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Always render the DataGrid with current data, and overlay a loading indicator if needed
    return (
      <div className="relative h-full w-full">
        <DataGrid
          columns={columns}
          data={data}
          loading={false}
          error={null}
          query={query}
          updateQuery={updateQuery}
          executeQuery={executeQuery}
          lastExecutedQuery={lastExecutedQuery}
          height="100%"
          width="100%"
        />
        
        {/* Only show loading overlay for non-initial loads (sorting, filtering, etc) */}
        {status === 'loading' && (
          <div className="absolute inset-0 bg-background/20 backdrop-blur-[0.5px] z-10 transition-opacity duration-150"></div>
        )}
      </div>
    );
  };

  // Render loading state while waiting for data initialization
  if (shouldShowInitializing) {
    return (
      <div className={cn('flex flex-col h-full w-full', className)}>
        <div className="flex h-full w-full items-center justify-center">
          <div className="text-center">
            <div className="mb-4">Initializing table data...</div>
          </div>
        </div>
      </div>
    );
  }
  
  // If we have data or a lastExecutedQuery, render the full UI with appropriate overlays
  // This ensures we maintain the UI during sort operations and other data changes
  return (
    <div className={cn('flex flex-col h-full w-full', className)}>
      <ResizablePanelGroup direction="horizontal" className="w-full h-full">
        {/* Main Table Panel */}
        <ResizablePanel defaultSize={75} minSize={40}>
          <NovaContainer
            className="flex-1 overflow-hidden"
            dataName={activeTable?.path || "Table"}
            columns={columnRefs}
            originalColumns={originalColumnRefs}
            query={query}
            updateQuery={updateQuery}
            rowCount={data?.table?.numRows || 0}
            executeQuery={() => executeQuery()}
            topRightSlot={
              <TopRightActions
                engine={engine}
                lastExecutedView={lastExecutedView}
                onSaveAsDataset={handleSaveAsDataset}
                onUpgradeClick={onUpgradeClick}
              />
            }
            lastExecutedView={lastExecutedView}
            onUpgradeClick={onUpgradeClick}
          >
            <div className="h-full flex flex-col">
              <div className="flex-1 overflow-hidden">
                {renderContent()}
              </div>
            </div>
          </NovaContainer>
        </ResizablePanel>
        
        {/* Resizable Handle */}
        <ResizableHandle withHandle />
        
        {/* AI Chat Panel */}
        <ResizablePanel defaultSize={25} minSize={20}>
          <NovaAIChat 
            engine={engine}
            className="h-full border-l"
            chatEndpoint={chatEndpoint}
            onRunSqlQuery={handleRunSqlQuery}
            initialPrompt={initialPrompt}
            subscriptionInfo={subscriptionInfo}
            onUpgradeClick={handleUpgradeFromChat}
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

/**
 * TableViewerNova Component
 * 
 * A simplified viewer that displays a table's contents using the provided engine and tableRef.
 */
export function TableViewerNova({
  engine,
  activeTable,
  className,
  chatEndpoint,
  initialPrompt,
  subscriptionInfo,
  onUpgradeClick
}: TableViewerNovaProps) {
  // Default mock subscription info if not provided
  const effectiveSubscriptionInfo = subscriptionInfo || {
    hasActiveSubscription: false,
    currentPlan: null,
    trialEndDate: null,
    isTrialing: false
  };

  return (
    <NovaStoreProvider engine={engine}>
      <TableViewerNovaInner 
        activeTable={activeTable} 
        className={className} 
        chatEndpoint={chatEndpoint}
        initialPrompt={initialPrompt}
        subscriptionInfo={effectiveSubscriptionInfo}
        onUpgradeClick={onUpgradeClick}
      />
    </NovaStoreProvider>
  );
} 